import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Server, Shield, Trash2, Plus, RefreshCw,
} from "lucide-react";

const DEFAULT_FORM = {
  imap_host: "mail.tlkadvisor.com",
  imap_port: 993,
  smtp_host: "mail.tlkadvisor.com",
  smtp_port: 465,
  email: "",
  password: "",
  delivery_email: "",
};

export default function EmailAccount() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [syncingId, setSyncingId] = useState(null);

  const load = async () => {
    const r = await api.get("/email-accounts");
    setAccounts(r.data);
    if (r.data.length === 0) setShowForm(true);
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = () => ({
    ...form,
    imap_port: Number(form.imap_port) || 993,
    smtp_port: Number(form.smtp_port) || 465,
    delivery_email: form.delivery_email?.trim() || null,
  });

  const testConn = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/email-account/test", buildPayload());
      setTestResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du test");
    } finally { setTesting(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/email-account", buildPayload());
      toast.success("Compte email connecté ✓");
      setForm(DEFAULT_FORM);
      setTestResult(null);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const disconnect = async (acc) => {
    if (!window.confirm(`Déconnecter ${acc.email} ? Les emails associés seront supprimés.`)) return;
    try {
      await api.delete(`/email-accounts/${acc.id}`);
      toast.success("Compte déconnecté");
      await load();
    } catch (err) { toast.error("Erreur"); }
  };

  const syncOne = async (acc) => {
    setSyncingId(acc.id);
    try {
      const r = await api.post("/emails/sync", null, { params: { limit: 30, account_id: acc.id } });
      toast.success(`${acc.email}: ${r.data.new} nouveaux • ${r.data.classified} classifiés`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec sync");
    } finally { setSyncingId(null); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="account-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline mb-1">// Connexions sécurisées</div>
          <h1 className="text-3xl font-bold tracking-tight mb-2" style={{fontFamily:"'Work Sans', sans-serif"}}>
            Comptes email
          </h1>
          <p className="text-gray-600 flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4 text-[#002FA7]" />
            Chaque compte est chiffré séparément (Fernet AES-128). Vous pouvez connecter plusieurs boîtes.
          </p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} data-testid="add-account-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] hover:bg-[#002277] text-white text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" /> Ajouter un compte
          </button>
        )}
      </div>

      {/* Connected accounts */}
      {accounts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="accounts-list">
          {accounts.map((acc) => (
            <div key={acc.id} className="bg-white border border-gray-200 p-5" data-testid={`account-card-${acc.id}`}>
              <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-200">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Connecté
                  </div>
                  <div className="font-medium text-gray-900 truncate">{acc.email}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => syncOne(acc)} disabled={syncingId === acc.id}
                    data-testid={`sync-account-${acc.id}`}
                    title="Synchroniser ce compte"
                    className="p-2 text-gray-600 hover:bg-gray-50 border border-gray-200 hover:border-gray-900 transition-colors disabled:opacity-50">
                    <RefreshCw className={`w-4 h-4 ${syncingId === acc.id ? "animate-spin" : ""}`} />
                  </button>
                  <button onClick={() => disconnect(acc)} data-testid={`delete-account-${acc.id}`}
                    title="Déconnecter"
                    className="p-2 text-red-700 hover:bg-red-50 border border-red-200 hover:border-red-700 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 text-xs">
                <Row icon={Server} label="IMAP" value={`${acc.imap_host}:${acc.imap_port}`} />
                <Row icon={Server} label="SMTP" value={`${acc.smtp_host}:${acc.smtp_port}`} />
                <Row icon={CheckCircle2} label="Résumés vers" value={acc.delivery_email || acc.email} />
                <Row icon={Server} label="Dernière sync" value={acc.last_sync_at ? new Date(acc.last_sync_at).toLocaleString("fr-FR") : "—"} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new account form */}
      {showForm && (
        <div className="bg-white border border-gray-200 p-6" data-testid="account-form-container">
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
            <div>
              <div className="overline mb-1">// Nouveau compte</div>
              <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans', sans-serif"}}>Connecter une boîte email</h3>
            </div>
            {accounts.length > 0 && (
              <button onClick={() => { setShowForm(false); setTestResult(null); setForm(DEFAULT_FORM); }}
                data-testid="cancel-form-btn"
                className="text-sm text-gray-500 hover:text-gray-900">Annuler</button>
            )}
          </div>
          <form onSubmit={save} className="space-y-5" data-testid="account-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Serveur IMAP entrant</label>
                <input value={form.imap_host} onChange={(e)=>update("imap_host", e.target.value)}
                  data-testid="imap-host-input"
                  className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none" required />
              </div>
              <div>
                <label className="overline block mb-2">Port IMAP (SSL)</label>
                <input type="number" value={form.imap_port} onChange={(e)=>update("imap_port", parseInt(e.target.value))}
                  data-testid="imap-port-input"
                  className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none" required />
              </div>
              <div>
                <label className="overline block mb-2">Serveur SMTP sortant</label>
                <input value={form.smtp_host} onChange={(e)=>update("smtp_host", e.target.value)}
                  data-testid="smtp-host-input"
                  className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none" required />
              </div>
              <div>
                <label className="overline block mb-2">Port SMTP (SSL/TLS)</label>
                <input type="number" value={form.smtp_port} onChange={(e)=>update("smtp_port", parseInt(e.target.value))}
                  data-testid="smtp-port-input"
                  className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none" required />
              </div>
              <div>
                <label className="overline block mb-2">Adresse email</label>
                <input type="email" value={form.email} onChange={(e)=>update("email", e.target.value)}
                  data-testid="account-email-input"
                  placeholder="contact@votre-domaine.com"
                  className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none" required />
              </div>
              <div>
                <label className="overline block mb-2">Mot de passe</label>
                <input type="password" value={form.password} onChange={(e)=>update("password", e.target.value)}
                  data-testid="account-password-input"
                  placeholder="••••••••"
                  className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none" required />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Adresse de réception des résumés (optionnel)</label>
                <input type="email" value={form.delivery_email} onChange={(e)=>update("delivery_email", e.target.value)}
                  data-testid="delivery-email-input"
                  placeholder="(par défaut = même adresse que celle ci-dessus)"
                  className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none" />
              </div>
            </div>

            {testResult && (
              <div className="grid grid-cols-2 gap-3" data-testid="test-result">
                <TestPill ok={testResult.imap.ok} label="IMAP" msg={testResult.imap.message} />
                <TestPill ok={testResult.smtp.ok} label="SMTP" msg={testResult.smtp.message} />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={testConn} disabled={testing} data-testid="test-connection-btn"
                className="px-5 py-2.5 bg-white border border-gray-300 hover:border-gray-900 text-sm font-medium transition-colors disabled:opacity-50">
                {testing ? "Test…" : "Tester la connexion"}
              </button>
              <button type="submit" disabled={loading} data-testid="save-account-btn"
                className="px-5 py-2.5 bg-[#002FA7] hover:bg-[#002277] text-white text-sm font-medium transition-colors disabled:opacity-50">
                {loading ? "Enregistrement…" : "Connecter →"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="overline mr-2">{label}</span>
        <span className="text-gray-800 font-mono break-all">{value}</span>
      </div>
    </div>
  );
}

function TestPill({ ok, label, msg }) {
  return (
    <div className={`flex items-start gap-3 p-3 border ${ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
      {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" /> : <XCircle className="w-5 h-5 text-red-700 shrink-0" />}
      <div className="text-xs">
        <div className={`font-semibold uppercase tracking-wider ${ok ? "text-emerald-700" : "text-red-700"}`}>{label} {ok ? "OK" : "Erreur"}</div>
        <div className="text-gray-700 mt-1">{msg}</div>
      </div>
    </div>
  );
}
